// istanbul ignore file
import { cx, VariantProps } from "cva";
import { ConditionalError } from "~/components/ConditionalError";
import { LeadGenerationFormBlock } from "~/components/LandingPageV2/schemas";
import { TurnstileWidget } from "~/components/TurnstileWidget/TurnstileWidget";
import { useRequestDemoForm } from "~/landing-pages/components/RequestDemo/useRequestDemoForm";
import {
  inputStyle,
  primaryTextColorStyle,
} from "~/landing-pages/shared-landing-page-styles";
import { useTurnstileHelper } from "~/lib/client/useTurnstileHelper";
import { getValueOrDefault } from "~/lib/shared/getValueOrDefault";
import { RequestDemoFormSchema } from "~/lib/shared/schemas/requestDemoSchema";
import { Body, Button } from "~/ui-library";

export type LeadGenerationFormProps = Pick<
  LeadGenerationFormBlock,
  | "enableDarkMode"
  | "openQuestion"
  | "openQuestionCharacterLimit"
  | "salesforceCampaignId"
> & {
  /** Class attached directly to the form element */
  className?: string;
  ctaText?: string;
  onFormSubmit: (formValues: RequestDemoFormSchema) => void;
};

export const LeadGenerationForm = ({
  className,
  ctaText,
  enableDarkMode,
  openQuestion,
  openQuestionCharacterLimit,
  salesforceCampaignId,
  onFormSubmit,
}: LeadGenerationFormProps) => {
  const {
    enableTurnstile,
    resetTurnstile,
    trackTurnstileError,
    setTurnstileResult,
  } = useTurnstileHelper();

  const {
    formState: { errors, isSubmitting, isSubmitSuccessful },
    handleSubmit,
    register,
    setValue,
  } = useRequestDemoForm({
    defaultFormValues: {
      salesforceCampaignId,
    },
    enableTurnstile,
  });

  const labelAppearance: VariantProps<
    typeof primaryTextColorStyle
  >["appearance"] = enableDarkMode ? "dark" : null;

  const conditionalErrorStyle = enableDarkMode
    ? "text-danger-350"
    : "text-danger";

  return (
    <form
      className={cx("mx-auto w-full", className)}
      onSubmit={handleSubmit(async (formData) => {
        await onFormSubmit(formData);
        resetTurnstile();
        setValue("turnstileResult", undefined);
      })}
    >
      <div className="grid grid-cols-2 gap-x-8 gap-y-6">
        <div className="col-span-2 md:col-span-1">
          <Body
            as="label"
            className={primaryTextColorStyle({
              appearance: labelAppearance,
              className: "block font-semibold",
            })}
          >
            First Name*
            <div className="mt-2.5">
              <input
                type="text"
                {...register("firstName")}
                className={inputStyle({
                  className: "w-full",
                  appearance: labelAppearance,
                })}
              />
              <ConditionalError
                className={conditionalErrorStyle}
                error={errors.firstName?.message}
              />
            </div>
          </Body>
        </div>
        <div className="col-span-2 md:col-span-1">
          <Body
            as="label"
            className={cx(
              "block font-semibold ",
              primaryTextColorStyle({ appearance: labelAppearance })
            )}
          >
            Last Name*
            <div className="mt-2.5">
              <input
                type="text"
                {...register("lastName")}
                className={inputStyle({
                  className: "w-full",
                  appearance: labelAppearance,
                })}
              />
              <ConditionalError
                className={conditionalErrorStyle}
                error={errors.lastName?.message}
              />
            </div>
          </Body>
        </div>
        <div className="sm:col-span-2">
          <Body
            as="label"
            className={cx(
              "block font-semibold ",
              primaryTextColorStyle({ appearance: labelAppearance })
            )}
          >
            Email Address*
            <div className="mt-2.5">
              <input
                type="email"
                {...register("email")}
                className={inputStyle({
                  className: "w-full",
                  appearance: labelAppearance,
                })}
              />
              <ConditionalError
                className={conditionalErrorStyle}
                error={errors.email?.message}
              />
            </div>
          </Body>
        </div>

        <div className="sm:col-span-2">
          <Body
            as="label"
            className={cx(
              "block font-semibold ",
              primaryTextColorStyle({ appearance: labelAppearance })
            )}
          >
            Title*
            <div className="mt-2.5">
              <input
                type="text"
                {...register("title")}
                className={inputStyle({
                  className: "w-full",
                  appearance: labelAppearance,
                })}
              />
              <ConditionalError
                className={conditionalErrorStyle}
                error={errors.title?.message}
              />
            </div>
          </Body>
        </div>

        <div className="sm:col-span-2">
          <Body
            as="label"
            className={cx(
              "block font-semibold ",
              primaryTextColorStyle({ appearance: labelAppearance })
            )}
          >
            Organization*
            <div className="mt-2.5">
              <input
                type="text"
                {...register("organization")}
                className={inputStyle({
                  className: "w-full",
                  appearance: labelAppearance,
                })}
              />
              <ConditionalError
                className={conditionalErrorStyle}
                error={errors.organization?.message}
              />
            </div>
          </Body>
        </div>

        {openQuestion?.length > 0 ? (
          <div className="sm:col-span-2">
            <Body
              as="label"
              className={cx(
                primaryTextColorStyle({ appearance: labelAppearance }),
                "mt-2.5 block font-semibold"
              )}
            >
              {openQuestion}
              <textarea
                {...register("howCanWeHelp")}
                rows={4}
                maxLength={getValueOrDefault(openQuestionCharacterLimit, 250)}
                className={inputStyle({
                  className: "w-full",
                  appearance: labelAppearance,
                })}
              />
            </Body>
          </div>
        ) : null}
      </div>

      {enableTurnstile ? (
        <div className="mt-10">
          <TurnstileWidget
            theme="light"
            onVerify={(token) => {
              setTurnstileResult(token);
              setValue("turnstileResult", token, { shouldValidate: true });
            }}
            onError={trackTurnstileError}
          />
          <ConditionalError
            error={errors.turnstileResult?.message}
            className={conditionalErrorStyle}
          />
        </div>
      ) : null}

      <div className="mt-10">
        <Button
          data-testid="submit-button"
          disabled={isSubmitSuccessful}
          fullWidth
          type="submit"
          updating={isSubmitting}
          variant="primary"
        >
          {getValueOrDefault(ctaText, "Let's talk")}
        </Button>
      </div>
    </form>
  );
};
